/**
 * Client for the Mandob (field sales rep) module (`/mandob`). A mandob is a
 * user + the `mandob` role + a profile (salary, pay scheme, commission %).
 */
import type {
  MandobCatalogItem, MandobCollectInput, MandobCollectionRow, MandobCreate, MandobCustomer,
  MandobCustomerCreateInput, MandobEarnings, MandobFullProfile, MandobOrderRow, MandobPayout,
  MandobPayoutRunInput, MandobProfile, MandobSellInput, MandobUpdate,
} from '@erp/shared';

import { api } from './api.js';

export type {
  MandobProfile, MandobEarnings, MandobCustomer, MandobCatalogItem, MandobOrderRow, MandobCollectionRow,
  MandobPayout, MandobFullProfile,
} from '@erp/shared';

export async function listMandobs(): Promise<MandobProfile[]> {
  return (await api().get<{ items: MandobProfile[] }>('/mandob')).data.items;
}

export async function getMandob(id: string): Promise<MandobProfile> {
  return (await api().get<MandobProfile>(`/mandob/${id}`)).data;
}

/** Full aggregated office profile of one rep (sales/collections/deliveries/customers/payouts). */
export async function getMandobFullProfile(id: string): Promise<MandobFullProfile> {
  return (await api().get<MandobFullProfile>(`/mandob/${id}/profile`)).data;
}

export async function createMandob(input: MandobCreate): Promise<MandobProfile> {
  return (await api().post<MandobProfile>('/mandob', input)).data;
}

export async function updateMandob(id: string, input: MandobUpdate): Promise<MandobProfile> {
  return (await api().patch<MandobProfile>(`/mandob/${id}`, input)).data;
}

/** The logged-in user's own mandob profile (null if they aren't a mandob). */
export async function getMyMandob(): Promise<MandobProfile | null> {
  return (await api().get<MandobProfile | null>('/mandob/me')).data;
}

// ── Field workspace (the logged-in rep's own surface) ──

export async function getMyEarnings(): Promise<MandobEarnings> {
  return (await api().get<MandobEarnings>('/mandob/me/earnings')).data;
}

export async function getMandobEarnings(id: string): Promise<MandobEarnings> {
  return (await api().get<MandobEarnings>(`/mandob/${id}/earnings`)).data;
}

export async function listMyCustomers(opts?: { all?: boolean; q?: string }): Promise<MandobCustomer[]> {
  const params = new URLSearchParams();
  if (opts?.all) params.set('all', '1');
  if (opts?.q) params.set('q', opts.q);
  const qs = params.toString();
  return (await api().get<{ items: MandobCustomer[] }>(`/mandob/me/customers${qs ? `?${qs}` : ''}`)).data.items;
}

/** Register a new customer in the field (auto-assigned to the rep). */
export async function createMyCustomer(input: MandobCustomerCreateInput): Promise<MandobCustomer> {
  return (await api().post<MandobCustomer>('/mandob/me/customers', input)).data;
}

export async function listMandobCatalog(q?: string): Promise<MandobCatalogItem[]> {
  const qs = q ? `?q=${encodeURIComponent(q)}` : '';
  return (await api().get<{ items: MandobCatalogItem[] }>(`/mandob/me/catalog${qs}`)).data.items;
}

export async function listMyOrders(): Promise<MandobOrderRow[]> {
  return (await api().get<{ items: MandobOrderRow[] }>('/mandob/me/orders')).data.items;
}

export async function listMyCollections(): Promise<MandobCollectionRow[]> {
  return (await api().get<{ items: MandobCollectionRow[] }>('/mandob/me/collections')).data.items;
}

export async function mandobSell(input: MandobSellInput): Promise<{ id: string; docNumber: string }> {
  return (await api().post<{ id: string; docNumber: string }>('/mandob/me/sell', input)).data;
}

export async function mandobCollect(input: MandobCollectInput): Promise<{ id: string; docNumber: string; posted: boolean }> {
  return (await api().post<{ id: string; docNumber: string; posted: boolean }>('/mandob/me/collect', input)).data;
}

export async function mandobDeliver(orderId: string): Promise<{ id: string; docNumber: string }> {
  return (await api().post<{ id: string; docNumber: string }>(`/mandob/me/orders/${orderId}/deliver`, {})).data;
}

// ── Office payouts ──

export async function runMandobPayout(id: string, input: MandobPayoutRunInput): Promise<MandobPayout> {
  return (await api().post<MandobPayout>(`/mandob/${id}/payout`, input)).data;
}

export async function listMandobPayouts(id: string): Promise<MandobPayout[]> {
  return (await api().get<{ items: MandobPayout[] }>(`/mandob/${id}/payouts`)).data.items;
}

/**
 * Take a payout back. The rep id is in the path as well as the payout's, so the
 * server can refuse a payout that is not this rep's.
 */
export async function removeMandobPayout(id: string, payoutId: string): Promise<void> {
  await api().delete(`/mandob/${id}/payouts/${payoutId}`);
}
